import React, { useState } from 'react';

import { BookingsSearchForm } from '@components/admin/booking/BookingsSearchForm';
import { BookingsTable } from '@components/admin/booking/BookingsTable';
import { Layout } from '@components/admin/Layout';
import { SectionTitle } from '@components/admin/SectionTitle';
import { Button } from '@components/Button';
import { ExportCSVDialog2 } from '@components/ExportCSVDialog2';
import { adminBookingInquiryTradeCondtionFilters } from '@data/adminBookingInquiry';
import { bookingsStrings } from '@data/bookings';
import { useSearch } from '@hooks/next/useSearch';
import { useRoutePlanList } from '@hooks/queries/dashboard/useRoutePlanList';
import { useUser } from '@hooks/queries/useUser';
import { DashboardPreAlertSearchType } from '@models/next/PreAlert';
import { PreAlertTypeType } from '@models/PreAlert';
import { makeMuiStyles, useCommonStyles } from '@styles';
import dayjs from 'dayjs';
import { uniq } from 'lodash-es';
import { pick } from 'remeda';

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    flex: 1,
    padding: '1.5rem 2rem',
  },
}));

export const AllBookings: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };
  const { search } = useSearch(DashboardPreAlertSearchType);

  const { checklist = [], ...preAlertSearch } = search;

  const initialSearch = {
    ...preAlertSearch,
    page: search.page ?? 1,
    page_size: search.page_size ?? 50,
  };

  const query = {
    ...initialSearch,
    ...(search.trade_condition &&
      adminBookingInquiryTradeCondtionFilters.get(search.trade_condition)),
    type: ['current', 'past_finished'] as PreAlertTypeType[],
  };

  const title = bookingsStrings.title()['all'];

  const user = useUser();

  const routePlans = useRoutePlanList({});

  const podOptions = uniq(routePlans.data?.map((routePlan) => routePlan.pod)).map((pod) => ({
    value: pod,
    label: pod,
  }));
  const polOptions = uniq(routePlans.data?.map((routePlan) => routePlan.pol)).map((pol) => ({
    value: pol,
    label: pol,
  }));

  const [UIState, setUIState] = useState(() => ({
    exportDialog: {
      open: false,
    },
  }));

  return (
    <Layout title={title} loading={user.isLoading} errors={[user.error]}>
      <ExportCSVDialog2
        open={UIState.exportDialog.open}
        onClose={() => {
          setUIState((state) => {
            return { ...state, exportDialog: { open: false } };
          });
        }}
        subject={bookingsStrings.booking}
        title={`${title} ${dayjs().format('YYYY-MM-DD HH:mm')}.csv`}
        baseUrl={'/dashboard/pre-alert/'}
        selected={checklist}
        query={query}
        defaultQuery={pick(query, ['type'])}
      />

      <div className={classes.container}>
        <SectionTitle
          actions={
            <Button
              onClick={() => {
                setUIState((state) => {
                  return { ...state, exportDialog: { open: true } };
                });
              }}
              color="primary"
              text={bookingsStrings.exportCSV}
              shape="round"
            />
          }
        >
          {title}
        </SectionTitle>

        <BookingsSearchForm
          tagFields={[
            'd2d_no',
            'consignee',
            'pol',
            'pod',
            'quantity',
            'member_salesperson',
            'salesperson_japan',
            'status',
          ]}
          rangeFields={[
            'eta',
            'etd',
            'delivery_instruction_preferred_delivery_date',
            'delivery_request_delivery_date',
          ]}
          fieldsInputOptions={{
            pol: { type: 'choose', options: polOptions },
            pod: { type: 'choose', options: podOptions },
          }}
        />

        <BookingsTable
          queryParams={query}
          columns={[
            'salesperson_japan_last_name',
            'consignee',
            'customs_instruction_status',
            'delivery_instruction_status',
            'customs_request_status',
            'salesperson_customs_last_name',
            'delivery_request_status',
            'do_status',
            'move_out_status',
            'etd',
            'eta',
            'vessel',
            'pol',
            'pod',
            'quantity',
            'invoice_status',
            'payment_slip_status',
            'member_salesperson_last_name',
            'invoice_tax_excluded_total_price',
            'payment_tax_excluded_total_price',
            'profit',
            'is_ledger_locked',
          ]}
          user={user.data}
          podOptions={podOptions}
          polOptions={polOptions}
          type="all"
        />
      </div>
    </Layout>
  );
};
